import { ResourceResolver } from "../../core/types";

import { fetchAccounts } from "./account";
import { fetchPeople } from "./person";
import { fetchServiceTeams } from "./serviceTeam";
import { fetchTaskTypes } from "./taskType";

// Registry implementation that supports both type and kind
class Registry<T> {
  private items = new Map<string, T>();
  private kindItems = new Map<string, Map<string, T>>();

  register(type: string, resolver: T): void {
    this.items.set(type, resolver);
  }

  registerWithKind(type: string, kind: string, resolver: T): void {
    if (!this.kindItems.has(type)) {
      this.kindItems.set(type, new Map<string, T>());
    }
    this.kindItems.get(type)!.set(kind, resolver);
  }

  get(type: string): T | undefined {
    return this.items.get(type);
  }

  getWithKind(type: string, kind: string): T | undefined {
    const kindMap = this.kindItems.get(type);
    return kindMap ? kindMap.get(kind) : undefined;
  }

  has(type: string): boolean {
    return this.items.has(type);
  }

  hasWithKind(type: string, kind: string): boolean {
    const kindMap = this.kindItems.get(type);
    return kindMap ? kindMap.has(kind) : false;
  }
}

// Create resource resolvers registry
const resourcesRegistry = new Registry<ResourceResolver>();

// Register standard type-based resolvers
resourcesRegistry.register("ServiceTeam", fetchServiceTeams);
resourcesRegistry.register("TaskType", fetchTaskTypes);

// Register kind-specific resolvers for Account type
resourcesRegistry.registerWithKind("Account", "external", fetchAccounts);
resourcesRegistry.registerWithKind(
  "Account",
  "manually-tracked",
  fetchAccounts
);
resourcesRegistry.registerWithKind("Account", "farther", fetchAccounts);
resourcesRegistry.registerWithKind("Account", "funding", fetchAccounts);

// Register kind-specific resolvers for Person type
resourcesRegistry.registerWithKind("Person", "Client", fetchPeople);
resourcesRegistry.registerWithKind("Person", "Advisor", fetchPeople);

// Register standalone Assignee (separate from Person type)
resourcesRegistry.register("Assignee", fetchPeople);

/**
 * Creates a default resolver for resource types without registered handlers
 */
function createDefaultResolver(type: string): ResourceResolver {
  return async (_auth: unknown, values: unknown) => {
    console.warn(
      `[FormEngine:ResourceRegistry] Using default resolver for "${type}"`
    );

    if (Array.isArray(values) && values.length > 0) {
      return values.map((v: unknown) => {
        const valueStr =
          typeof v === "object" && v !== null && "value" in v
            ? String((v as { value: string | number }).value)
            : String(v);

        return {
          type,
          label: `${type}: ${valueStr}`,
          value: valueStr,
        };
      });
    }

    return [];
  };
}

/**
 * Gets the resource resolver for a specific resource type
 */
export function getResourceResolver(type: string): ResourceResolver {
  if (!type) {
    console.error("[FormEngine:ResourceRegistry] No resource type provided");
    return createDefaultResolver("unknown");
  }

  const resource = resourcesRegistry.get(type);

  if (!resource) {
    return createDefaultResolver(type);
  }

  return resource;
}

/**
 * Gets the resource resolver for a specific resource type and kind
 */
export function getResourceResolverWithKind(
  type: string,
  kind: string
): ResourceResolver {
  if (!type || !kind) {
    console.error("[FormEngine:ResourceRegistry] Missing type or kind");
    return createDefaultResolver("unknown");
  }

  const resource = resourcesRegistry.getWithKind(type, kind);

  if (!resource) {
    // Fall back to type-only resolver if kind-specific resolver is not found
    const typeResolver = resourcesRegistry.get(type);
    if (typeResolver) {
      return typeResolver;
    }
    return createDefaultResolver(type);
  }

  return resource;
}
